import { ListingCard, type Listing as VisualListing } from "./ListingCard";
import { ChevronRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { listingsAPI } from "@/lib/api/listings";
import { Listing as ApiListing } from "@/types/listing";
import { useMarketplace } from "./marketplace-context";
import { Link } from "react-router-dom";

function timeAgo(dateString: string) {
  const now = new Date();
  const date = new Date(dateString);
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (isNaN(seconds) || seconds < 0) return 'some time ago';

  let interval = Math.floor(seconds / 31536000);
  if (interval >= 1) return `${interval} yr${interval === 1 ? '' : 's'} ago`;
  interval = Math.floor(seconds / 2592000);
  if (interval >= 1) return `${interval} mo${interval === 1 ? '' : 's'} ago`;
  interval = Math.floor(seconds / 86400);
  if (interval >= 1) return `${interval} day${interval === 1 ? '' : 's'} ago`;
  interval = Math.floor(seconds / 3600);
  if (interval >= 1) return `${interval} hr${interval === 1 ? '' : 's'} ago`;
  interval = Math.floor(seconds / 60);
  if (interval >= 1) return `${interval} min${interval === 1 ? '' : 's'} ago`;
  return 'just now';
}

const categoryPlaceholders: Record<string, string> = {
  CARS: 'https://picsum.photos/seed/cars-listing/400/400',
  BIKES: 'https://picsum.photos/seed/bikes-listing/400/400',
  MOBILES: 'https://picsum.photos/seed/mobile-listing/400/400',
  REAL_ESTATE: 'https://picsum.photos/seed/real-estate-listing/400/400',
  RENT_ROOMMATES: 'https://picsum.photos/seed/rent-roommates-listing/400/400',
  BUS_TRUCKS_AUTOS_VEHICLES: 'https://picsum.photos/seed/bus-truck-auto-listing/400/400',
  AUTOMOBILE_ACCESSORIES: 'https://picsum.photos/seed/auto-accessories/400/400',
  PETS_AND_HOBBIES: 'https://picsum.photos/seed/pets-hobbies/400/400',
  COMMUNITY_AND_EVENTS: 'https://picsum.photos/seed/community-events/400/400',
  FURNITURE_INTERIORS: 'https://picsum.photos/seed/furniture-interiors/400/400',
  ELECTRONICS_AND_APPLIANCES: 'https://picsum.photos/seed/electronics-appliances/400/400',
  PERSONAL_AND_COMMUNITY: 'https://picsum.photos/seed/personal-community/400/400',
  JOBS_AND_SERVICES: 'https://picsum.photos/seed/jobs-services/400/400',
  FASHION_AND_LIFESTYLE: 'https://picsum.photos/seed/fashion-lifestyle/400/400',
  SAFETY_PROTECTION: 'https://picsum.photos/seed/safety-protection/400/400',
  BUSINESS_FINANCIAL: 'https://picsum.photos/seed/business-financial/400/400',
  PROFESSIONAL_SERVICES: 'https://picsum.photos/seed/professional-services/400/400',
  INSURANCE_LEGAL: 'https://picsum.photos/seed/insurance-legal/400/400',
  MAINTENANCE_AND_REPAIR: 'https://picsum.photos/seed/maintenance-repair/400/400',
  PERSONAL_SERVICES: 'https://picsum.photos/seed/personal-services/400/400',
  SALES_AUCTIONS: 'https://picsum.photos/seed/sales-auctions/400/400',
  GIFTS_SPECIAL_OCCASIONS: 'https://picsum.photos/seed/gifts-occasions/400/400',
  TRAVEL_ENTERTAINMENT: 'https://picsum.photos/seed/travel-entertainment/400/400',
  WATER_FILTRATION: 'https://picsum.photos/seed/water-filtration/400/400',
  SECURITY_GUARDS: 'https://picsum.photos/seed/security-guards/400/400',
  OTHER_SERVICES: 'https://picsum.photos/seed/other-services/400/400',
};

function getCategoryPlaceholder(category?: string, id?: string) {
  const cat = category?.toUpperCase() || '';
  return categoryPlaceholders[cat] || `https://picsum.photos/seed/${id || 'default'}/400/400`;
}

function toVisualListing(api: ApiListing): VisualListing {
  return {
    id: api.id,
    image: api.images?.[0] || getCategoryPlaceholder(api.category, api.id),
    title: api.title,
    price: `₹${api.price.toLocaleString('en-IN')}`,
    location: api.location,
    postedAt: timeAgo(api.createdAt),
    featured: Boolean(api.featured || api.price > 50000), // Show featured tag if marked featured or premium items
    description: api.description,
  };
}

function SectionHeader({ title, subtitle, link }: { title: string; subtitle?: string; link: string }) {
  return (
    <div className="mb-5 flex items-end justify-between">
      <div>
        <h2 className="font-heading text-2xl font-bold text-foreground md:text-3xl">{title}</h2>
        {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      <Link to={link} className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline">
        See all <ChevronRight className="h-4 w-4" />
      </Link>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card">
      <div className="aspect-[4/3] animate-pulse bg-secondary" />
      <div className="space-y-2 p-3.5">
        <div className="h-5 w-24 animate-pulse rounded bg-secondary" />
        <div className="h-4 w-full animate-pulse rounded bg-secondary" />
        <div className="h-3 w-2/3 animate-pulse rounded bg-secondary" />
      </div>
    </div>
  );
}

function EmptyState({ label }: { label: string }) {
  return (
    <div className="col-span-full rounded-2xl border border-dashed border-border bg-card p-10 text-center">
      <p className="font-heading text-base font-semibold text-foreground">No listings found</p>
      <p className="mt-1 text-sm text-muted-foreground">{label}</p>
    </div>
  );
}

export function FeaturedListings() {
  const { category, city } = useMarketplace();
  const cityFilter = city === "All India" ? undefined : city;
  const categoryFilter = category === "All Categories" ? undefined : category;

  const { data, isLoading } = useQuery({
    queryKey: ["listings", "featured", categoryFilter, cityFilter],
    queryFn: async () => {
      // 1. Fetch featured listings from backend endpoint
      let featured: ApiListing[] = [];
      try {
        const res = await listingsAPI.getFeatured(12, categoryFilter || undefined, cityFilter || undefined);
        featured = (res?.listings ?? res ?? []) as ApiListing[];
      } catch {
        // endpoint not available – fall through to fallback
      }

      if (featured.length > 0) return { listings: featured, isNearby: false };

      // 2. Fallback: fetch regular listings with city filter
      let localItems: ApiListing[] = [];
      try {
        const res = await listingsAPI.getAll({
          category: categoryFilter || undefined,
          location: cityFilter || undefined,
          limit: 12
        });
        localItems = (res?.content ?? res?.items ?? []) as ApiListing[];
      } catch {
        // fall through
      }

      // If we already have 12 or no city filter, return local items
      if (localItems.length >= 12 || !cityFilter) {
        return { listings: localItems, isNearby: false };
      }

      // 3. If city filter returned fewer than 12, fetch all active listings to fill up
      try {
        const allRes = await listingsAPI.getAll({
          category: categoryFilter || undefined,
          limit: 12
        });
        const allItems = (allRes?.content ?? allRes?.items ?? []) as ApiListing[];
        const seen = new Set(localItems.map(i => i.id));
        const combined = [...localItems, ...allItems.filter(i => !seen.has(i.id))].slice(0, 12);
        return { listings: combined, isNearby: localItems.length < combined.length };
      } catch {
        return { listings: localItems, isNearby: false };
      }
    },
  });

  const items = (data?.listings ?? []).map(toVisualListing);
  const isNearby = data?.isNearby ?? false;
  const seeAllLink = categoryFilter 
    ? `/listings?category=${categoryFilter.toLowerCase().replace(' ', '-')}`
    : '/listings';

  const subtitle = cityFilter
    ? `Premium ads in ${cityFilter}${isNearby ? ' and Near Locations' : ''}${categoryFilter ? ` · ${categoryFilter}` : ''}`
    : `Premium ads across India${categoryFilter ? ` · ${categoryFilter}` : ''}`;

  return (
    <section className="mx-auto max-w-7xl px-4 py-5 md:py-6">
      <SectionHeader 
        title="Featured listings" 
        subtitle={subtitle} 
        link={seeAllLink}
      />
      <div id="listings-top" className="scroll-mt-32" />
      <div className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {isLoading
          ? Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="w-[260px] shrink-0 snap-start sm:w-[280px]">
                <SkeletonCard />
              </div>
            ))
          : items.length === 0
          ? <EmptyState label="Try changing category or city." />
          : items.map((l) => (
              <div key={l.id} className="w-[260px] shrink-0 snap-start sm:w-[280px]">
                <ListingCard listing={l} compact />
              </div>
            ))}
      </div>
    </section>
  );
}

export function FreshListings() {
  const { category, city } = useMarketplace();
  const cityFilter = city === "All India" ? undefined : city;
  const categoryFilter = category === "All Categories" ? undefined : category;

  const { data, isLoading } = useQuery({
    queryKey: ["listings", "fresh", categoryFilter, cityFilter],
    queryFn: async () => {
      let localItems: ApiListing[] = [];
      try {
        const res = await listingsAPI.getAll({
          category: categoryFilter || undefined,
          location: cityFilter || undefined,
          limit: 24
        });
        localItems = (res?.content ?? res?.items ?? []) as ApiListing[];
      } catch {
        // fall through
      }

      if (localItems.length >= 24 || !cityFilter) {
        return { listings: localItems, isNearby: false };
      }

      // If city filter returned fewer than 24, also fetch without location to fill up
      try {
        const fallbackRes = await listingsAPI.getAll({
          category: categoryFilter || undefined,
          limit: 24
        });
        const fallbackItems = (fallbackRes?.content ?? fallbackRes?.items ?? []) as ApiListing[];
        const seen = new Set(localItems.map(i => i.id));
        const combined = [...localItems, ...fallbackItems.filter(i => !seen.has(i.id))].slice(0, 24);
        return { listings: combined, isNearby: localItems.length === 0 };
      } catch {
        return { listings: localItems, isNearby: false };
      }
    },
  });

  const items = (data?.listings ?? []).map(toVisualListing);
  const isNearby = data?.isNearby ?? false;
  const seeAllLink = categoryFilter 
    ? `/listings?category=${categoryFilter.toLowerCase().replace(' ', '-')}`
    : '/listings';

  const subtitle = cityFilter
    ? `Newly posted in ${cityFilter}${isNearby ? ' and Near Locations' : ''}.`
    : 'Newly posted.';

  return (
    <section className="mx-auto max-w-7xl px-4 py-5 md:py-6">
      <SectionHeader 
        title="Fresh recommendations" 
        subtitle={subtitle} 
        link={seeAllLink}
      />
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {isLoading
          ? Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)
          : items.length === 0
          ? <EmptyState label="No ads match the selected filters yet." />
          : items.map((l) => <ListingCard key={l.id} listing={l} />)}
      </div>
      {items.length > 0 && (
        <div className="mt-8 flex justify-center">
          <Link to="/listings" className="rounded-xl border border-border bg-background px-6 py-3 text-sm font-semibold text-foreground transition hover:border-primary hover:text-primary">
            Load more listings
          </Link>
        </div>
      )}
    </section>
  );
}